import { useContext } from 'react';
import MenuPhotoSwipe from '@discover-components/restaurant/menu/photoSwipe';
import useLightbox from '@discover-components/restaurant/menu/useLightbox';
import { findMaxLastEdited, getMenuPages } from '@discover-components/restaurant/menu/utils';
import { timeFromNow } from '@discover-lib/dateFormats';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Title from '@firsttable/web-components/atoms/Title';
import IconSpoonM from '@firsttable/web-components/atoms/Icon/IconSpoonM';
import SilverstripeContext from '@/components/context/Silverstripe';
import { getRestaurantMenuLink, getRestaurantMenuPaths } from '@/lib/restaurantMenu';

export default function RestaurantMenu({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const lightbox = useLightbox();
  const router = useRouter();
  const { page: restaurant } = useContext(SilverstripeContext);
  const pages = getMenuPages(restaurant.menus || { edges: [] }).filter((page) => page.url);
  const externalUrl = getRestaurantMenuLink(restaurant);
  const { restaurantPath } = getRestaurantMenuPaths(
    restaurant.slug,
    router.isReady ? router.asPath : ''
  );
  const title = `${restaurant.menuTitle} menu`;
  const lastEdited = findMaxLastEdited(restaurant.menus);

  return (
    <section className="min-w-0" aria-labelledby="restaurant-menu-heading">
      <Title as="h2" id="restaurant-menu-heading" mb={24}>
        Menu
      </Title>
      <Link
        href={restaurantPath}
        scroll={false}
        prefetch={false}
        className="mb-6 inline-block break-words underline">
        <IconSpoonM mr="10px" top="-1px" color="gold.900" aria-hidden="true" />
        {restaurant.menuTitle} restaurant — reviews, dining offers and bookings
      </Link>
      {lastEdited && (
        <p className="mb-4 text-xs text-gray-600">Last updated {timeFromNow(lastEdited)}</p>
      )}
      <p className="mb-6">
        Menus and prices can change. Check with the restaurant for current details.
      </p>
      {pages.length > 0 && (
        <ul className="m-0 grid list-none gap-6 p-0">
          {pages.map((page, index) => (
            <li key={`${page.url}-${index}`}>
              <a
                href={page.src}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  lightbox.open(index);
                  onOpenChange(true);
                }}
                aria-haspopup="dialog"
                className="block underline">
                <Image
                  src={page.src}
                  alt={`${restaurant.menuTitle} menu, page ${index + 1}`}
                  width={page.w}
                  height={page.h}
                  sizes="(min-width: 992px) 650px, 100vw"
                  className="h-auto w-full"
                />
              </a>
            </li>
          ))}
        </ul>
      )}
      <MenuPhotoSwipe
        items={pages}
        isOpen={lightbox.isOpen}
        index={lightbox.index}
        showBackground={lightbox.showBackground}
        onChange={lightbox.onChange}
        onClose={() => {
          lightbox.close();
          onOpenChange(false);
        }}
        lastUpdated={lastEdited}
      />
      {externalUrl && (
        <section className="mt-6">
          <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="underline">
            Open {restaurant.menuTitle}&apos;s menu on its original website
          </a>
          <iframe src={externalUrl} title={title} className="mt-4 h-[75vh] w-full border-0" />
        </section>
      )}
    </section>
  );
}
